import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Col, Modal, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";

import { actionTypeSelector, importEmployeesSelector } from "../reducers";
import { getLocaleText } from "../actions/datagrid";
import { contextMenu, defaultColDef, multiRow, sideBar, statusBar, statusBarSelected } from "./grid";
import Loading from "./loaders/loading";
import { CheckCircleIcon, CrossCircleIcon } from "./icons";
import { exportProcountor, importProcountor } from "../actions/persons/importemployees";
import { getAllPersons } from "../actions/persons/personsgetall";
import { listProcountorQuery } from "../api";
import { queryClient } from "../main";
import { Button } from "./Button.tsx";
import Switch from "./Switch";

export function ProcountorModal({ hideModal }) {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const actionType = useSelector(actionTypeSelector);
  const importEmployeesResult = useSelector(importEmployeesSelector);

  const [isImport, setIsImport] = useState();

  const [updateExisting, setUpdateExisting] = useState(true);
  const [createNew, setCreateNew] = useState(true);
  const [showResults, setShowResults] = useState(false);
  const [showResultLoading, setShowResultLoading] = useState(false);
  const [tableData, setTableData] = useState([]);

  const gridRef = useRef(null);
  const savedSelectionRef = useRef([]);

  const persons = useQuery(listProcountorQuery(true)).data;
  const [selectedRowsCount, setSelectedRowsCount] = useState(0);
  const hasSelection = selectedRowsCount > 0;
  const hasAction = updateExisting || createNew;

  const [sentCount, setSentCount] = useState(0);
  const [processedCount, setProcessedCount] = useState(0);

  const onSelectionChanged = useCallback(({ api }) => {
    const selectedRows = api.getSelectedRows();
    setSelectedRowsCount(selectedRows.length);
  }, []);

  const saveSelection = () => {
    const selectedRows = gridRef.current?.api.getSelectedRows() || [];
    savedSelectionRef.current = selectedRows.map((row) => ({
      personId: row.personId ? String(row.personId) : null,
      procountorId: row.settings?.procountorId ? String(row.settings.procountorId) : null,
    }));
  };

  const onRowDataUpdated = useCallback(({ api }) => {
    const saved = savedSelectionRef.current;
    if (saved.length === 0) return;

    saved.forEach(({ personId, procountorId }) => {
      let node = personId ? api.getRowNode(personId) : null;

      if (!node && procountorId) {
        api.forEachNode((rowNode) => {
          if (!node && String(rowNode.data?.settings?.procountorId) === procountorId) {
            node = rowNode;
          }
        });
      }

      if (node) {
        node.setSelected(true);
      }
    });

    savedSelectionRef.current = [];
  }, []);

  useEffect(() => {
    if (actionType === "IMPORT_EMPLOYEES_SUCCEEDED" && importEmployeesResult) {
      const { created, updated, createFailed, updateFailed } = importEmployeesResult;
      const processedCountNumber = created + updated + createFailed + updateFailed;
      setProcessedCount(processedCountNumber);
      setShowResultLoading(false);
      setTableData(importEmployeesResult.employees);

      queryClient.invalidateQueries({ queryKey: ["listProcountor", true] });
      dispatch(getAllPersons());
    } else if (actionType === "IMPORT_EMPLOYEES_FAIL") {
      setShowResultLoading(false);
    }
  }, [actionType, importEmployeesResult, dispatch]);

  const handleExport = () => {
    setIsImport(false);
    setTableData([]);
    dispatch({
      type: "EXPORT_EMPLOYEES_RESET",
      payload: { importEmployees: null },
    });

    const selectedRows = gridRef.current?.api.getSelectedRows() || [];
    const personIds = selectedRows
      .filter((person) => person.callingName === "h" || person.callingName === "b")
      .map((person) => person.personId);

    dispatch(exportProcountor(updateExisting, createNew, personIds));
    setShowResultLoading(true);
    setShowResults(true);

    saveSelection();
    gridRef.current?.api?.deselectAll();
  };

  const handleImport = () => {
    setIsImport(true);
    setTableData([]);
    dispatch({
      type: "IMPORT_EMPLOYEES_RESET",
      payload: { importEmployees: null },
    });

    const selectedRows = gridRef.current?.api.getSelectedRows() || [];
    const personIds = selectedRows
      .filter((person) => person.callingName === "p" || person.callingName === "b")
      .map((person) => Number(person.settings?.procountorId));

    setSentCount(personIds.length);
    dispatch(importProcountor(updateExisting, createNew, personIds));
    setShowResultLoading(true);
    setShowResults(true);

    saveSelection();
    gridRef.current?.api?.deselectAll();
  };

  const getRowId = ({ data }) => String(data.personId || data.settings?.procountorId);

  const importStatusValueFormatter = (params) => {
    if (params.value === true || params.value === "true") {
      return t("ImportStatus_SUCCESS", "Onnistui");
    } else if (params.value === false || params.value === "false") {
      return t("ImportStatus_FAIL", "Epäonnistui");
    }
  };

  const importActionValueFormatter = ({ value }) => (value === "CREATE" ? t("Create", "Luo") : t("Update", "Päivitä"));

  const importErrorValueFormatter = (params) => {
    if (params && params.data && params.data.errorMessages) {
      return params.data.errorMessages.map(handleErrorMessages);
    }
  };

  const handleErrorMessages = (value) => {
    const key = value.key;
    const defaultValue = value.defaultValue;
    if (key === "NOT_LOCALIZED_UNEXPECTED_ERROR") {
      return defaultValue;
    } else {
      return t(key, defaultValue);
    }
  };

  const ImportStatusRenderer = (params) => {
    let statusIcon;
    if (params.value === true) {
      statusIcon = <CheckCircleIcon />;
    } else if (params.value === false) {
      statusIcon = <CrossCircleIcon />;
    }

    const statusTooltip = <Tooltip id="tooltip-right">{importStatusValueFormatter(params)}</Tooltip>;
    return (
      <>
        <OverlayTrigger key="right" placement="right" overlay={statusTooltip}>
          <span>{statusIcon}</span>
        </OverlayTrigger>
      </>
    );
  };

  const gridComponents = {
    importStatusRenderer: ImportStatusRenderer,
  };

  const columnDefs = [
    {
      headerName: t("Status", "Tila"),
      field: "success",
      valueFormatter: importStatusValueFormatter,
      filter: "agSetColumnFilter",
      filterParams: {
        valueFormatter: importStatusValueFormatter,
      },
      cellRenderer: "importStatusRenderer",
    },
    {
      headerName: t("Number", "Numero"),
      field: "nr",
      filter: "agNumberColumnFilter",
    },
    {
      headerName: t("Name", "Nimi"),
      field: "name",
    },
    {
      headerName: t("Action", "Toiminto"),
      field: "action",
      valueFormatter: importActionValueFormatter,
      filter: "agSetColumnFilter",
      filterParams: {
        valueFormatter: importActionValueFormatter,
      },
    },
    {
      headerName: t("Errors", "Virheet"),
      field: "errorMessages",
      valueGetter: importErrorValueFormatter,
      tooltipValueGetter: importErrorValueFormatter,
      filter: "agSetColumnFilter",
    },
  ];

  const personColDef = useMemo(
    () => [
      {
        field: "nr",
        headerName: t("NumberAbbr", "Nro"),
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => params.value || "",
        //headerCheckboxSelection: true,
        //checkboxSelection: true,
      },
      {
        headerName: t("Name", "Nimi"),
        field: "name",
      },
      {
        headerName: t("JobTitle", "Tehtävänimike"),
        field: "employmentDetails.profession",
      },
      {
        headerName: t("OnHr", "HR:ssä"),
        cellDataType: "boolean",
        filter: "agSetColumnFilter",
        valueGetter: ({ data }) => data.callingName === "h" || data.callingName === "b",
      },
      {
        headerName: t("OnProcountor", "Procountorissa"),
        cellDataType: "boolean",
        filter: "agSetColumnFilter",
        valueGetter: ({ data }) => data.callingName === "p" || data.callingName === "b",
      },
    ],
    [t],
  );

  const getContextMenuItems = useCallback(
    (params) => {
      return contextMenu(t, params);
    },
    [t],
  );

  const memoizedDefaultColDef = useMemo(() => ({ ...defaultColDef, enableCellChangeFlash: true }), [defaultColDef]);

  return (
    <Modal show onHide={hideModal} backdrop="static" size="xl" fullscreen="lg-down">
      <Modal.Header closeButton>
        <Modal.Title>{t("ProcountorImportExport", "Procountor tuonti/vienti")}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="d-flex gap-4 mb-2">
          <Switch
            id="createNew"
            label={t("CreateNewPersons", "Luo uudet henkilöt")}
            checked={createNew}
            onChange={(e) => setCreateNew(e.target.checked)}
          />
          <Switch
            id="updateExisting"
            label={t("UpdateExisting", "Päivitä olemassa olevat")}
            checked={updateExisting}
            onChange={(e) => setUpdateExisting(e.target.checked)}
          />
        </div>

        {Array.isArray(persons) && persons.length === 0 && (
          <Alert variant="warning" className="ml-auto mb-3 py-2 px-4 shadow-sm" style={{ width: "fit-content" }}>
            {t("NoPersonsInProcountor", "Procountorissa ei ole työntekijöitä")}
          </Alert>
        )}

        <Row className="styles-ag flex-grow-1 ag-theme-material" style={{ height: "400px" }}>
          <Col>{t("SelectPersons", "Valitse henkilöt")}</Col>
          {persons ? (
            <AgGridReact
              ref={gridRef}
              columnDefs={personColDef}
              defaultColDef={memoizedDefaultColDef}
              sideBar={sideBar}
              rowData={persons}
              getContextMenuItems={getContextMenuItems}
              statusBar={statusBarSelected}
              getRowId={getRowId}
              getLocaleText={(params) => getLocaleText(t, params)}
              rowSelection={multiRow}
              onSelectionChanged={onSelectionChanged}
              onRowDataUpdated={onRowDataUpdated}
              animateRows
              suppressCellFocus
              debug={import.meta.env.DEV}
            />
          ) : (
            <div style={{ padding: "2rem", textAlign: "center", width: "100%" }}>{t("Loading", "Ladataan...")}</div>
          )}
        </Row>

        {showResults && (
          <>
            <hr className="mb-12" />

            <Row className="mb-1">
              <Row className="mb-1">
                <Col sm="12">
                  <h3>{t("Results", "Tulokset")}</h3>
                </Col>
              </Row>

              {showResultLoading && (
                <div className="d-flex justify-content-center">
                  <Loading show={showResultLoading} />
                </div>
              )}

              {importEmployeesResult && (
                <>
                  <Row className="mb-1">
                    <Col sm="12">
                      <Alert key="info" variant="info">
                        <Row className="mb-1">
                          <Col sm="3">
                            {isImport
                              ? t("PersonsImported", "Henkilöitä tuotiin")
                              : t("PersonsExported", "Henkilöitä vietiin")}
                            :
                          </Col>
                          <Col sm="1">
                            <strong>{importEmployeesResult.created}</strong>
                          </Col>
                          <Col sm="1"></Col>
                          <Col sm="3">
                            {isImport
                              ? t("PersonsImportsFailed", "Henkilön tuontia epäonnistui")
                              : t("PersonsExportsFailed", "Henkilön vientiä epäonnistui")}
                            :
                          </Col>
                          <Col sm="1">
                            <strong style={{ color: "red" }}>{importEmployeesResult.createFailed}</strong>
                          </Col>
                          <Col sm="1"></Col>
                          <Col sm="1">{t("Took", "Kesto")}:</Col>
                          <Col sm="1">
                            <strong>{importEmployeesResult.took} ms</strong>
                          </Col>
                        </Row>
                        <Row className="mb-1">
                          <Col sm="3">{t("PersonsUpdated", "Henkilöitä päivitettiin")}:</Col>
                          <Col sm="1">
                            <strong>{importEmployeesResult.updated}</strong>
                          </Col>
                          <Col sm="1"></Col>
                          <Col sm="3">{t("PersonsUpdatesFailed", "Henkilön päivitystä epäonnistui")}:</Col>
                          <Col sm="1">
                            <strong style={{ color: "red" }}>{importEmployeesResult.updateFailed}</strong>
                          </Col>
                        </Row>
                      </Alert>
                    </Col>
                  </Row>
                  {showResults && processedCount < sentCount && (
                    <Row className="mb-2">
                      <Col>
                        <Alert variant="warning" className="m-0">
                          {t(
                            "ImportMismatchDetail",
                            "Vain {{processed}} {{sent}}:stä valitusta henkilöstä tuotiin onnistuneesti.",
                            {
                              sent: sentCount,
                              processed: tableData.length,
                            },
                          )}
                        </Alert>
                      </Col>
                    </Row>
                  )}
                  <Row className="styles-ag flex-grow-1 ag-theme-material" style={{ height: "400px" }}>
                    <AgGridReact
                      components={gridComponents}
                      columnDefs={columnDefs}
                      defaultColDef={defaultColDef}
                      sideBar={sideBar}
                      rowData={tableData}
                      getContextMenuItems={getContextMenuItems}
                      statusBar={statusBar}
                      getRowId={getRowId}
                      getLocaleText={(params) => getLocaleText(t, params)}
                      animateRows
                      suppressCellFocus
                      enableCellTextSelection
                      onFirstDataRendered={(params) => params.api.autoSizeAllColumns()}
                      debug={import.meta.env.DEV}
                    />
                  </Row>
                </>
              )}
            </Row>
          </>
        )}
      </Modal.Body>

      <Modal.Footer style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="cancel" onClick={hideModal}>
          {t("Close", "Sulje")}
        </Button>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button variant="success" onClick={handleImport} disabled={!hasSelection || !hasAction}>
            {t("ImportToHr", "Tuo HR:ään")}
          </Button>
          <Button variant="primary" onClick={handleExport} disabled={!hasSelection || !hasAction}>
            {t("ExportToProcountor", "Vie Procountoriin")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
